import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, IconButton, Paper, Card, CardActionArea,
  CardContent, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import ApartmentIcon from '@mui/icons-material/Apartment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

// ── Küçük yardımcılar ──────────────────────────────────────────────
const fileType = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (['doc', 'docx'].includes(ext)) return 'Word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'Excel';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'Resim';
  return ext ? ext.toUpperCase() : '—';
};
const fmtTL = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '—');

// Önizleme için dosyanın hangi biçimde gösterileceğini belirler.
const previewKind = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['txt', 'md', 'json', 'csv', 'log', 'xml'].includes(ext)) return 'text';
  if (['mp4', 'webm', 'ogv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
  return 'other';
};

// ── Üç bilgi tablosunun kolon tanımları ────────────────────────────
const FEE_COLUMNS = [
  { key: 'fee_name', label: 'Ücret Adı', required: true },
  { key: 'amount', label: 'Tutar', type: 'number', align: 'right', format: fmtTL },
  { key: 'description', label: 'Açıklama', multiline: true },
];
const BANK_COLUMNS = [
  { key: 'bank_name', label: 'Banka Adı', required: true },
  { key: 'branch_name', label: 'Şube Adı' },
  { key: 'iban', label: 'IBAN No', mono: true, placeholder: 'TR00 0000 0000 0000 0000 0000 00' },
];
const CONTACT_COLUMNS = [
  { key: 'full_name', label: 'Ad Soyad', required: true },
  { key: 'title', label: 'Görevi' },
  { key: 'phone', label: 'Telefon' },
  { key: 'email', label: 'E-Posta', type: 'email' },
];

export default function AdminTgbTracking() {
  const [teknokents, setTeknokents] = useState([]);
  const [selected, setSelected] = useState(null); // seçili teknokent objesi

  // Liste: yeni teknokent ekleme
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  // Detay verileri
  const [documents, setDocuments] = useState([]);
  const [fees, setFees] = useState([]);
  const [banks, setBanks] = useState([]);
  const [contacts, setContacts] = useState([]);

  const docInputRef = useRef(null);

  // Önizleme durumu: { name, kind, url, text, downloadUrl, error }
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef(null);

  // ── Yüklemeler ───────────────────────────────────────────────────
  const fetchTeknokents = useCallback(() => {
    axios.get(`${API_BASE}/teknokents`)
      .then((res) => setTeknokents(res.data || []))
      .catch((err) => console.error('TGB listesi alınamadı:', err));
  }, []);

  useEffect(() => { fetchTeknokents(); }, [fetchTeknokents]);

  const loadDetail = useCallback((id) => {
    axios.get(`${API_BASE}/teknokents/${id}/documents`).then((r) => setDocuments(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/teknokents/${id}/fees`).then((r) => setFees(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/teknokents/${id}/banks`).then((r) => setBanks(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/teknokents/${id}/contacts`).then((r) => setContacts(r.data || [])).catch(() => {});
  }, []);

  const openTeknokent = (t) => {
    setSelected(t);
    loadDetail(t.id);
  };

  const backToList = () => {
    setSelected(null);
    fetchTeknokents(); // sayaçları tazele
  };

  // ── Teknokent ekle / sil ─────────────────────────────────────────
  const submitAdd = () => {
    const name = newName.trim();
    if (!name) return;
    axios.post(`${API_BASE}/teknokents`, { name })
      .then(() => { setAddOpen(false); setNewName(''); fetchTeknokents(); })
      .catch((err) => console.error('Teknokent eklenemedi:', err));
  };

  const deleteTeknokent = (t) => {
    if (!window.confirm(`"${t.name}" teknokentini ve tüm belge/bilgi kayıtlarını silmek istediğinize emin misiniz?`)) return;
    axios.delete(`${API_BASE}/teknokents/${t.id}`)
      .then(() => { backToList(); })
      .catch((err) => console.error('Teknokent silinemedi:', err));
  };

  // ── Döküman yükle / sil ──────────────────────────────────────────
  const onDocPicked = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append('file', file);
    axios.post(`${API_BASE}/teknokents/${selected.id}/documents`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => loadDetail(selected.id))
      .catch((err) => console.error('Döküman yüklenemedi:', err));
  };

  const deleteDocument = (id) => {
    if (!window.confirm('Bu dökümanı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/teknokents/documents/${id}`)
      .then(() => setDocuments((prev) => prev.filter((d) => d.id !== id)))
      .catch((err) => console.error('Döküman silinemedi:', err));
  };

  // ── Ücret / Banka / İletişim kayıtları (ortak CRUD) ──────────────
  const saveInfo = (kind, id, values) =>
    (id
      ? axios.put(`${API_BASE}/teknokents/${kind}/${id}`, values)
      : axios.post(`${API_BASE}/teknokents/${selected.id}/${kind}`, values)
    ).then(() => loadDetail(selected.id));

  const deleteInfo = (kind, id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/teknokents/${kind}/${id}`)
      .then(() => loadDetail(selected.id))
      .catch((err) => console.error('Kayıt silinemedi:', err));
  };

  // İndirme uçları admin-token ister; düz <a> bu header'ı taşımaz, bu yüzden
  // axios ile blob olarak çekip (global x-admin-token ile) kaydettiriyoruz.
  const download = async (url, fileName) => {
    try {
      const { data } = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('İndirme hatası:', err);
    }
  };

  // ── Önizleme ─────────────────────────────────────────────────────
  // İndirme ucu admin-token istediği için dosyayı axios ile blob olarak çekip
  // object URL üzerinden gösteriyoruz (doğrudan <img>/<iframe> src'i header taşımaz).
  const releasePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  useEffect(() => releasePreviewUrl, []);

  const openPreview = async (url, fileName) => {
    const kind = previewKind(fileName);
    releasePreviewUrl();
    setPreview({ name: fileName, kind, url: '', text: '', downloadUrl: url, error: '' });
    if (kind === 'other') return; // Word/Excel vb. — tarayıcıda gösterilemiyor
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(url, { responseType: 'blob' });
      if (kind === 'text') {
        const text = await data.text();
        setPreview((p) => (p ? { ...p, text } : p));
      } else {
        const blobUrl = URL.createObjectURL(data);
        previewUrlRef.current = blobUrl;
        setPreview((p) => (p ? { ...p, url: blobUrl } : p));
      }
    } catch (err) {
      console.error('Önizleme hatası:', err);
      setPreview((p) => (p ? { ...p, error: 'Dosya yüklenemedi.' } : p));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    releasePreviewUrl();
    setPreview(null);
    setPreviewLoading(false);
  };

  const previewDialog = (
    <Dialog open={!!preview} onClose={closePreview} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1, wordBreak: 'break-word' }}>{preview?.name}</Typography>
          <IconButton onClick={closePreview} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 240 }}>
        <PreviewBody preview={preview} loading={previewLoading} />
      </DialogContent>
      <DialogActions>
        {preview?.url && (
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
          >
            Yeni sekmede aç
          </Button>
        )}
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => preview && download(preview.downloadUrl, preview.name)}
        >
          İndir
        </Button>
        <Button onClick={closePreview} variant="contained">Kapat</Button>
      </DialogActions>
    </Dialog>
  );

  // ── LİSTE GÖRÜNÜMÜ ───────────────────────────────────────────────
  if (!selected) {
    return (
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>TGB Takip</Typography>
          <Typography variant="body2" color="text.secondary">Toplam: {teknokents.length} teknokent</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setNewName(''); setAddOpen(true); }}>
            Yeni Teknokent
          </Button>
        </Stack>

        {teknokents.length === 0 ? (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <ApartmentIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography color="text.secondary" sx={{ mt: 1 }}>Henüz teknokent eklenmemiş.</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
            {teknokents.map((t) => (
              <Card key={t.id} sx={{ borderLeft: 5, borderLeftColor: 'primary.main' }}>
                <CardActionArea onClick={() => openTeknokent(t)}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ApartmentIcon color="primary" />
                      <Typography variant="h6" sx={{ flex: 1, wordBreak: 'break-word' }}>{t.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
                      <Chip size="small" variant="outlined" label={`${t.document_count || 0} döküman`} />
                      <Chip size="small" variant="outlined" label={`${t.fee_count || 0} ücret`} />
                      <Chip size="small" variant="outlined" label={`${t.bank_count || 0} banka`} />
                      <Chip size="small" variant="outlined" label={`${t.contact_count || 0} kişi`} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Yeni Teknokent</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Teknokent İsmi"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
              fullWidth
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)}>İptal</Button>
            <Button onClick={submitAdd} variant="contained" disabled={!newName.trim()}>Kaydet</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── DETAY GÖRÜNÜMÜ ───────────────────────────────────────────────
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={backToList}><ArrowBackIcon /></IconButton>
        <ApartmentIcon color="primary" />
        <Typography variant="h4" sx={{ flexGrow: 1, wordBreak: 'break-word' }}>{selected.name}</Typography>
        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => deleteTeknokent(selected)}>
          Teknokenti Sil
        </Button>
      </Stack>

      {/* ── Dökümanlar ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1 }}>Dökümanlar</Typography>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => docInputRef.current?.click()}>
            Dosya Yükle
          </Button>
          <input ref={docInputRef} type="file" hidden onChange={onDocPicked} />
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <FileTable
          rows={documents}
          emptyText="Henüz döküman yüklenmemiş."
          onPreview={(f) => openPreview(`${API_BASE}/teknokents/documents/${f.id}/download`, f.file_name)}
          onDownload={(f) => download(`${API_BASE}/teknokents/documents/${f.id}/download`, f.file_name)}
          onDelete={(f) => deleteDocument(f.id)}
        />
      </Paper>

      {/* ── Ücretler ve Açıklamalar ── */}
      <InfoSection
        title="Ücretler ve Açıklamalar"
        icon={<PaymentsIcon color="primary" />}
        columns={FEE_COLUMNS}
        rows={fees}
        emptyText="Henüz ücret kaydı eklenmemiş."
        addLabel="Ücret Ekle"
        dialogTitle="Ücret"
        onSave={(id, values) => saveInfo('fees', id, values)}
        onDelete={(id) => deleteInfo('fees', id)}
      />

      {/* ── Banka Bilgileri ── */}
      <InfoSection
        title="Banka Bilgileri"
        icon={<AccountBalanceIcon color="primary" />}
        columns={BANK_COLUMNS}
        rows={banks}
        emptyText="Henüz banka bilgisi eklenmemiş."
        addLabel="Banka Ekle"
        dialogTitle="Banka Bilgisi"
        onSave={(id, values) => saveInfo('banks', id, values)}
        onDelete={(id) => deleteInfo('banks', id)}
      />

      {/* ── İletişim Kişisi ── */}
      <InfoSection
        title="İletişim Kişisi"
        icon={<ContactPhoneIcon color="primary" />}
        columns={CONTACT_COLUMNS}
        rows={contacts}
        emptyText="Henüz iletişim kişisi eklenmemiş."
        addLabel="Kişi Ekle"
        dialogTitle="İletişim Kişisi"
        onSave={(id, values) => saveInfo('contacts', id, values)}
        onDelete={(id) => deleteInfo('contacts', id)}
      />

      {previewDialog}
    </Box>
  );
}

// Ekle / düzenle / sil yapabilen jenerik bilgi bölümü.
// Ücretler, Banka Bilgileri ve İletişim Kişisi bölümlerinin üçü de bunu kullanır.
function InfoSection({ title, icon, columns, rows, emptyText, addLabel, dialogTitle, onSave, onDelete }) {
  const [editing, setEditing] = useState(null); // { id: number|null, values: {} }
  const [saving, setSaving] = useState(false);

  const blank = () => Object.fromEntries(columns.map((c) => [c.key, '']));

  const openAdd = () => setEditing({ id: null, values: blank() });
  const openEdit = (row) => setEditing({
    id: row.id,
    values: Object.fromEntries(columns.map((c) => [c.key, row[c.key] ?? ''])),
  });
  const close = () => { setEditing(null); setSaving(false); };

  const setField = (key, value) =>
    setEditing((e) => (e ? { ...e, values: { ...e.values, [key]: value } } : e));

  const valid = columns.every((c) => !c.required || String(editing?.values?.[c.key] ?? '').trim());

  const submit = () => {
    if (!editing || !valid) return;
    setSaving(true);
    onSave(editing.id, editing.values)
      .then(close)
      .catch((err) => { console.error('Kayıt kaydedilemedi:', err); setSaving(false); });
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        {icon}
        <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>{addLabel}</Button>
      </Stack>
      <Divider sx={{ mb: 1 }} />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'}>{c.label}</TableCell>
              ))}
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align || 'left'}
                    sx={{
                      whiteSpace: c.multiline ? 'pre-wrap' : 'normal',
                      wordBreak: 'break-word',
                      ...(c.align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : {}),
                      ...(c.mono ? { fontFamily: 'monospace' } : {}),
                    }}
                  >
                    {c.format ? c.format(row[c.key]) : (row[c.key] || '—')}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Düzenle">
                    <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton size="small" color="error" onClick={() => onDelete(row.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? `${dialogTitle} Düzenle` : `Yeni ${dialogTitle}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {columns.map((c, i) => (
              <TextField
                key={c.key}
                autoFocus={i === 0}
                label={c.label}
                type={c.type === 'number' ? 'number' : (c.type || 'text')}
                inputProps={c.type === 'number' ? { step: '0.01', min: 0 } : undefined}
                placeholder={c.placeholder}
                required={c.required}
                multiline={c.multiline}
                maxRows={c.multiline ? 4 : undefined}
                value={editing?.values?.[c.key] ?? ''}
                onChange={(e) => setField(c.key, e.target.value)}
                fullWidth
                size="small"
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>İptal</Button>
          <Button onClick={submit} variant="contained" disabled={!valid || saving}>
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// Dosya tablosu (Dökümanlar).
function FileTable({ rows, emptyText, onPreview, onDownload, onDelete, showCompany = false }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Dosya Adı</TableCell>
          {showCompany && <TableCell>Firma</TableCell>}
          <TableCell>Tür</TableCell>
          <TableCell>Yükleme Tarihi</TableCell>
          <TableCell align="right">İşlem</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((f) => (
          <TableRow key={f.id} hover>
            <TableCell sx={{ wordBreak: 'break-word' }}>{f.file_name}</TableCell>
            {showCompany && <TableCell>{f.company_name || '—'}</TableCell>}
            <TableCell><Chip size="small" label={fileType(f.file_name)} /></TableCell>
            <TableCell>{fmtDateTime(f.uploaded_at)}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {onPreview && (
                <Tooltip title="Önizle">
                  <IconButton size="small" color="info" onClick={() => onPreview(f)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="İndir">
                <IconButton size="small" color="primary" onClick={() => onDownload(f)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sil">
                <IconButton size="small" color="error" onClick={() => onDelete(f)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={showCompany ? 5 : 4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              {emptyText}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// Önizleme gövdesi: dosya türüne göre uygun görüntüleyiciyi seçer.
function PreviewBody({ preview, loading }) {
  if (!preview) return null;

  if (preview.error) {
    return <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{preview.error}</Typography>;
  }

  if (preview.kind === 'other') {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
        <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography color="text.secondary" align="center">
          Bu dosya türü ({fileType(preview.name)}) tarayıcıda önizlenemiyor.
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Görüntülemek için dosyayı indirmeniz gerekiyor.
        </Typography>
      </Stack>
    );
  }

  if (loading || (!preview.url && !preview.text)) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (preview.kind === 'image') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          component="img"
          src={preview.url}
          alt={preview.name}
          sx={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 1 }}
        />
      </Box>
    );
  }

  if (preview.kind === 'pdf') {
    return (
      <Box sx={{ width: '100%', height: { xs: '65vh', md: '75vh' } }}>
        <iframe
          src={preview.url}
          title={preview.name}
          style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
        />
      </Box>
    );
  }

  if (preview.kind === 'text') {
    return (
      <Box
        component="pre"
        sx={{
          m: 0, p: 2, borderRadius: 1,
          bgcolor: 'action.hover',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontSize: '0.875rem', maxHeight: '70vh', overflow: 'auto',
        }}
      >
        {preview.text}
      </Box>
    );
  }

  if (preview.kind === 'video') {
    return <Box component="video" src={preview.url} controls sx={{ width: '100%', maxHeight: '70vh' }} />;
  }

  return <Box component="audio" src={preview.url} controls sx={{ width: '100%' }} />;
}
